import { utils } from '@hh/global.js';
const { reRenderShopifySections, fetchCartHTML } = utils;

class RepAIManager extends HTMLElement {
  constructor() {
    super();

    this.repInitialized = false;
    this.isInlineMode = false;
    // Store original parent so we can return the container on navigation away
    this._originalParent = null;
    this._cartListenerRegistered = false;

    this.customCSSTemplate = this.querySelector('[data-repai-injected-css]');
    this.chatBanner = this.querySelector('.gifting-concierge-chat-banner');
    this.giftBanner = this.querySelector('.gifting-concierge-gift-banner');
    this.giftBannerCloseButton = this.querySelector('[data-gift-banner-close]');

    this.handleGiftBannerClose = this.handleGiftBannerClose.bind(this);
    this._onContainerReady = this._onContainerReady.bind(this);
    this._onRepLoad = this._onRepLoad.bind(this);
    this._onLinkClick = this._onLinkClick.bind(this);
    this._onPopState = this._onPopState.bind(this);
  }

  connectedCallback() {
    this.giftBannerCloseButton?.addEventListener('click', this.handleGiftBannerClose);

    // Hide chat button when footer is in view
    const footer = document.querySelector('footer');
    if (footer) {
      this.footerObserver = new IntersectionObserver(this.onFooterIntersection.bind(this), {
        threshold: 0,
        rootMargin: '100px 0px 0px 0px',
      });
      this.footerObserver.observe(footer);
    }

    // Listen for the inline container becoming available.
    // This fires on fresh load AND on every Swup SPA navigation to gifting concierge,
    // because the script is inside the Swup-replaced content.
    document.addEventListener('rep:containerReady', this._onContainerReady);

    // Listen for the chat app being ready (fires once per app initialization)
    document.addEventListener('rep:load', this._onRepLoad);

    // Intercept navigation to move the chat container back to body BEFORE Swup
    // destroys the content. Capture phase ensures we run before Swup's listener.
    document.addEventListener('click', this._onLinkClick, true);
    window.addEventListener('popstate', this._onPopState);

    // Check if the inline container already exists (fresh load where the inline script
    // ran before this module — the script is parsed inline in the Swup content,
    // while this module loads asynchronously).
    const inlineContainer = document.querySelector('#repWebClientContainerParent');
    if (inlineContainer) {
      this._onContainerReady();
    }

    this.initBanners();
  }

  disconnectedCallback() {
    this.giftBannerCloseButton?.removeEventListener('click', this.handleGiftBannerClose);
    this.footerObserver?.disconnect();
    document.removeEventListener('rep:containerReady', this._onContainerReady);
    document.removeEventListener('rep:load', this._onRepLoad);
    document.removeEventListener('click', this._onLinkClick, true);
    window.removeEventListener('popstate', this._onPopState);

    this.restoreFloatingMode();
  }

  /**
   * Capture-phase click handler: if a same-origin navigating link is clicked while in inline mode,
   * move the chat container back to body BEFORE Swup replaces the content.
   * Skips non-navigating anchors (href="#", empty, javascript:, etc.) and links where
   * another handler already called preventDefault() is handled by the origin check.
   */
  _onLinkClick(e) {
    if (!this.isInlineMode) return;

    const link = e.target.closest('a');
    if (!link || !link.href) return;

    try {
      const url = new URL(link.href, window.location.origin);
      // Skip non-navigating links: fragments, javascript:, same-page anchors
      if (url.protocol === 'javascript:') return;
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.hash) return;

      this.restoreFloatingMode();
    } catch {
      // Invalid URL — ignore
    }
  }

  /**
   * Handle browser back/forward navigation while in inline mode.
   */
  _onPopState() {
    if (this.isInlineMode) {
      this.restoreFloatingMode();
    }
  }

  /**
   * Called when the gifting concierge page's inline container appears in the DOM.
   * Handles both fresh load and Swup SPA navigation.
   */
  _onContainerReady() {
    // Prevent widget from auto-opening on load (in both scenarios)
    try { window.sessionStorage.removeItem('vfIsOpen'); } catch {}

    // Hide chat immediately so it doesn't render before custom styles are injected
    try { window.sessionStorage.setItem('vfHide', 'true'); } catch {}

    const chatContainer = document.querySelector('#repWebClientContainer');
    const inlineParent = document.querySelector('#repWebClientContainerParent');

    if (!inlineParent) return;

    if (chatContainer && !inlineParent.contains(chatContainer)) {
      // SPA navigation: the chat exists elsewhere (floating mode in document.body).
      // Physically move it into the inline container.
      this._originalParent = chatContainer.parentElement;
      inlineParent.appendChild(chatContainer);

      // Apply inline styles to the container
      chatContainer.style.width = '100%';
      chatContainer.style.height = '100%';
      chatContainer.style.position = 'relative';
      chatContainer.style.display = 'block';
      chatContainer.style.zIndex = '0';

      this.isInlineMode = true;
      this.repInitialized = false; // Allow re-initialization for the new page context

      // Inject custom CSS and configure, then show
      this.injectCustomStyles();

      const fullScreenEnabled = this.getAttribute('data-full-screen-enabled') === 'true';
      window.rep?.fullScreenEnabled(fullScreenEnabled);

      if (!this._cartListenerRegistered) {
        this.setupAddToCartListener();
      }

      // Fresh start for a clean conversation, then reveal
      if (typeof window.rep?.freshStart === 'function') {
        window.rep.freshStart();
      }
      window.rep?.show();
      this.repInitialized = true;
    } else if (chatContainer && inlineParent.contains(chatContainer)) {
      // Fresh page load: main.js already created the container inside inlineParent.
      // Store document.body as the original parent so restoreFloatingMode() can
      // move the widget back when navigating away.
      this._originalParent = document.body;
      this.isInlineMode = true;
    } else {
      // Container doesn't exist yet (fresh load, vfLoader hasn't run yet).
      // The app will be created inside inlineParent by main.js (reads window.repContainer.selector).
      // Store document.body so restoreFloatingMode() works after rep:load fires.
      this._originalParent = document.body;
      this.isInlineMode = true;
    }
  }

  /**
   * Called when window.rep is fully initialized (rep:load event).
   * On fresh load this fires once after the app mounts.
   * Runs on ALL pages — custom styles, cart listener and fullscreen config are needed globally.
   * show() only applies in inline mode (floating widget shows/hides via its own button).
   */
  _onRepLoad() {
    if (this.repInitialized) return;
    this.repInitialized = true;

    // Inject merchant CSS into shadow DOM on every page
    this.injectCustomStyles();

    const fullScreenEnabled = this.getAttribute('data-full-screen-enabled') === 'true';
    window.rep?.fullScreenEnabled(fullScreenEnabled);

    if (!this._cartListenerRegistered) {
      this.setupAddToCartListener();
    }

    // Inline-mode-only: reveal the widget (it was hidden to prevent flash before styles)
    if (this.isInlineMode) {
      window.rep?.show();
    }
  }

  /**
   * Move the chat container back to its original location (floating mode).
   * Called before Swup replaces content or on navigation away from gifting concierge.
   */
  restoreFloatingMode() {
    if (!this.isInlineMode) return;

    const chatContainer = document.querySelector('#repWebClientContainer');
    if (chatContainer && this._originalParent) {
      this._originalParent.appendChild(chatContainer);

      // Restore floating styles
      chatContainer.style.height = '0px';
      chatContainer.style.width = '0px';
      chatContainer.style.position = 'fixed';
      chatContainer.style.display = 'block';
      chatContainer.style.zIndex = '2147483647';

      // Clear the repContainer config so the floating widget works normally
      delete window['repContainer'];

      // Reset stickyConfigStore via public API — this re-reads window.repContainer.config
      // (now undefined) and removes the defaultOpen/hideControls guard that blocks close()
      window.rep?.config({});

      // Close the chat now that the guard is lifted
      window.rep?.close();
    }

    this._originalParent = null;
    this.isInlineMode = false;
    this.repInitialized = false;
  }

  injectCustomStyles() {
    const container = document.querySelector('#repWebClientContainer');
    if (container?.shadowRoot && !container.shadowRoot.querySelector('[data-merchant-css]')) {
      const style = document.createElement('style');
      style.textContent = this.customCSSTemplate?.innerHTML || '';
      style.setAttribute('data-merchant-css', 'true');
      container.shadowRoot.appendChild(style);
      return true;
    }
    return false;
  }

  triggerCustomFlow(flowKey) {
    window.rep.triggerFlow({
      flowType: 'ENTRY_FLOW',
      flowKey,
      chatopened: true,
    });
  }

  setupAddToCartListener() {
    if (this._cartListenerRegistered) return;
    this._cartListenerRegistered = true;

    window.rep.on('updateCartByProducts', (e) => {
      setTimeout(async () => {
        const updatedCartHTMLResponse = await fetchCartHTML();
        if (updatedCartHTMLResponse) {
          reRenderShopifySections({ sections: updatedCartHTMLResponse });
          document.dispatchEvent(new CustomEvent('recommendations:refresh'));

          setTimeout(() => {
            document.querySelector('hh-mini-cart')?.open();
          }, 0);
        }
      }, 2000); // Delay because updateCartByProducts fires before cart is updated
    });
  }

  updateBannerDisplayState() {
    this.chatBanner?.classList.add('hide');
    this.chatBanner?.setAttribute('inert', '');

    this.giftBanner?.classList.add('revealed');

    document.documentElement.style.setProperty('--gifting-concierge-toggle-icon-display-override', 'block');
  }

  initBanners() {
    const viewedUntilTimeStamp = window.localStorage.getItem('gifting_concierge_viewed_until');
    if (viewedUntilTimeStamp && viewedUntilTimeStamp > Date.now()) {
      this.updateBannerDisplayState();
    } else {
      this.chatBanner?.classList.remove('hidden');
      setTimeout(() => {
        this.updateBannerDisplayState();
        const duration = 7 * 24 * 60 * 60 * 1000; // 1 week
        window.localStorage.setItem('gifting_concierge_viewed_until', Date.now() + duration);
      }, 5000);
    }
  }

  handleGiftBannerClose() {
    this.giftBanner?.classList.add('hidden');
  }

  onFooterIntersection([entry]) {
    const { isIntersecting } = entry;

    const chatButton =
      document.querySelector('#repWebClientContainer')?.shadowRoot?.querySelector('.vf-container__minimized') ??
      document.querySelector('#gorgias-chat-container');
    if (!chatButton) return;

    if (isIntersecting) {
      chatButton.classList.add('intersecting-with-footer');
    } else {
      chatButton.classList.remove('intersecting-with-footer');
    }
  }
}

customElements.define('hh-repai-manager', RepAIManager);
